'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useState } from 'react';

import { SocialAuthButtons } from '@/components/SocialAuthButtons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authClient } from '@/lib/auth-client';

export default function SignInComponent() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await authClient.signIn.email({
      email,
      password,
    });

    setIsLoading(false);

    if (result.error) {
      setError(result.error.message ?? 'Sign in failed. Please check your credentials.');
      return;
    }

    router.push('/u/home');
  }

  return (
    <div className="w-full max-w-sm">
      <div className="rounded-lg bg-gray-800 p-8">
        <h2 className="mb-6 text-center text-xl font-semibold text-gray-100">Sign in to bettr.golf</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-300">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="border-gray-600 bg-gray-700 text-gray-200 placeholder:text-gray-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-gray-300">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              className="border-gray-600 bg-gray-700 text-gray-200 placeholder:text-gray-500"
            />
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}

          <Button type="submit" disabled={isLoading} className="w-full bg-primary-700 hover:bg-primary-600">
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <div className="mt-4 space-y-2 text-center text-sm">
          <Link href="/forgot-password" className="text-gray-400 hover:text-primary-500">
            Forgot your password?
          </Link>
          <p className="text-gray-500">
            Don&apos;t have an account?{' '}
            <Link href="/sign-up" className="text-gray-300 hover:text-primary-500">
              Sign up
            </Link>
          </p>
        </div>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-600" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-gray-800 px-2 text-gray-500">or</span>
          </div>
        </div>

        <SocialAuthButtons />
      </div>
    </div>
  );
}
